<template>
    <div id="app">
        <div
            :class="{
                'w-100': omitClasses,
                'app-container': !omitClasses,
                'app-container--wide': !omitClasses && isWideRoute
            }"
        >
            <router-view v-if="((user && storeAuthenticated) || isOpenRoute) && !showBillingWall" />
            <template v-else>
                <store-selector class="store-selector auth-error" />
                <div class="auth-error-container">
                    <!-- Store is installed & authenticated but has no active subscription -->
                    <div v-if="showBillingWall" class="billing-wall">
                        <div>
                            Customily requires an active subscription on <b>{{ currentShop }}</b
                            >.
                        </div>
                        <template v-if="billingWallStoreCanSubscribe">
                            <div class="mt-2">Please approve the subscription charges to continue using Customily.</div>
                            <button
                                class="btn btn-primary mt-3"
                                :disabled="billingResolving"
                                @click="openBillingSubscriptionLink"
                            >
                                {{ billingResolving ? 'Please wait…' : 'Update subscription' }}
                            </button>
                            <div v-if="billingError" class="mt-2 text-danger">{{ billingError }}</div>
                        </template>
                        <div v-else class="mt-2">
                            Please contact <a href="mailto:hello@customily.com">hello@customily.com</a> to complete your
                            store’s billing information.
                        </div>
                    </div>
                    <template v-else-if="store">
                        <div>
                            Customily is not {{ msgVerb.past }} on <b>{{ store }}</b
                            >, please {{ msgVerb.present }} it to use the app.
                        </div>

                        <template v-if="isAmazonStore">
                            <div class="region-selector">
                                <button
                                    class="btn btn-primary d-block mb-2 mt-2"
                                    @click="(e) => openInstallLink(e, 'na')"
                                >
                                    Install Customily on Amazon NA
                                </button>
                                <button class="btn btn-primary d-block mb-2" @click="(e) => openInstallLink(e, 'eu')">
                                    Install Customily on Amazon EU
                                </button>
                                <button class="btn btn-primary d-block mb-2" @click="(e) => openInstallLink(e, 'fe')">
                                    Install Customily on Amazon FE
                                </button>
                            </div>
                        </template>
                        <button v-else class="btn btn-primary mt-3" @click="openInstallLink">
                            {{ msgVerb.capitalized }} Customily on {{ store }}
                        </button>
                    </template>
                    <div v-else>
                        <div v-if="wooUserHasntInstalledYet">
                            <div class="content">
                                <p>
                                    Please Download and install Customily's plugin on your Woocommerce store to get
                                    started:
                                </p>
                            </div>
                            <br />
                            <a
                                href="https://cdn.customily.com/Customily_Woocommerce_V2.zip"
                                class="woo-install-btn download-link"
                                target="_blank"
                            >
                                <span><strong>DOWNLOAD PLUGIN</strong></span>
                            </a>
                            <div class="instructions">
                                <p>&nbsp;</p>
                                <p class="help">
                                    Need help installing the plugin? Get instructions
                                    <a
                                        href="https://docs.woocommerce.com/document/installing-uninstalling-woocommerce/"
                                        rel="noopener"
                                        target="_blank"
                                    >
                                        here </a
                                    >.
                                </p>
                            </div>
                        </div>
                        <div v-else>
                            Customily is not {{ msgVerb.past }} on your store, please {{ msgVerb.present }} it to use
                            the app.
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { capitalize } from 'lodash';
import { AxiosError } from 'axios';
import { useStore } from '@/store';
import { getUser, isStoreAuthenticated } from '@/api/shopify/user';
import { StoreType } from '@extTypes/api/customily/user';
import {
    isWooCommerceStore,
    isEtsyStore as checkIsEtsyStore,
    isShoplazzaStore,
    isAmazonStore as checkIsAmazonStore,
    getStoreType
} from '@/utils/stores';
import { getEtsyLogin, setEtsyRequestToken } from '@/api/shopify/etsy';
import { forceStoreResubscribe } from '@/api/shopify/payment';
import StoreSelector from '@/components/StoreSelector.vue';
import config from '@/config/config';

const { state, commit } = useStore();
const route = useRoute();
const router = useRouter();

const wideRoutes = new Set(['/orders', '/products_preview']);

const storeAuthenticated = ref(true);
const isOpenRoute = ref(false);
const isEtsyStore = ref(false);
const isAmazonStore = ref(false);
const omitClasses = ref(false);
const loginUrl = ref('');
const tokenSecret = ref('');
const tokenState = ref('');
const billingResolving = ref(false);
const billingError = ref('');

const user = computed(() => state.user);
const store = computed(() => state.store);

const appInstallUrl = computed((): string => {
    if (!store.value || !user.value?.customilyUser) return '';

    const baseUrl = config.APP_SHOPIFY_BASE_URL;
    const shopifyProductPersonalizerBaseUrl = config.SHOPIFY_PRODUCT_PERSONALIZER_BASE_URL;
    const userStores = user.value.customilyUser.Stores;

    if (isWooCommerceStore(store.value, userStores)) return `https://${store.value}/wp-login.php`;
    if (isEtsyStore.value) return loginUrl.value;
    if (isShoplazzaStore(store.value, userStores)) return `${baseUrl}/shoplazza/auth?shop=${store.value}`;
    if (isAmazonStore.value) return `${baseUrl}/amazon/auth?shop=${store.value}`;
    return `${shopifyProductPersonalizerBaseUrl}/auth/login?shop=${store.value}`;
});

const msgVerb = computed(() => {
    const verb = isEtsyStore.value ? 'connect' : 'install';
    return {
        present: verb,
        past: `${verb}ed`,
        capitalized: capitalize(verb)
    };
});

const wooUserHasntInstalledYet = computed(() => {
    const isWooUser = Boolean(new URL(location.href).searchParams.get('isWooUser'));
    //this means that our api call throwed 401 when trying to get the user, because the user hasn't installed
    return isWooUser && !user.value;
});

/**
 * Store the current page is authenticating/gating for: the shop query param when present, otherwise the
 * selected store. Single source of truth so the store authentication check, the billing wall decision and
 * its message always refer to the same store.
 */
const currentShop = computed((): string => (route.query.shop as string) || store.value);

const showBillingWall = computed((): boolean => {
    if (isOpenRoute.value || !storeAuthenticated.value || !user.value || !currentShop.value) {
        return false;
    }
    return user.value.customilyUser.Stores.find((s) => s.Url === currentShop.value)?.BillingBlocked ?? false;
});

// Shopify and Shoplazza stores can self-resolve billing from the app.
// Every other store type is directed to contact support instead.
const billingWallStoreCanSubscribe = computed((): boolean => {
    const type = currentShop.value
        ? getStoreType(currentShop.value, user.value?.customilyUser.Stores ?? [])
        : undefined;
    return type === StoreType.Shopify || type === StoreType.Shoplazza;
});

const billingSubscriptionUrl = computed((): string => {
    if (!currentShop.value || !user.value) return '';
    const type = getStoreType(currentShop.value, user.value.customilyUser.Stores);

    if (type === StoreType.Shoplazza) {
        return `${config.APP_SHOPIFY_BASE_URL}/shoplazza/auth?shop=${currentShop.value}&useAppScopes=true`;
    }

    // Shopify goes through the product-personalizer app managed-install entry point
    return `${config.SHOPIFY_PRODUCT_PERSONALIZER_BASE_URL}/auth/login?shop=${currentShop.value}`;
});

const isWideRoute = computed(() => {
    const currentRoute = route.path || '';
    return wideRoutes.has(currentRoute) || currentRoute.startsWith('/reviewProducts/');
});

const openBillingSubscriptionLink = async () => {
    if (billingResolving.value || !currentShop.value) return;

    billingResolving.value = true;
    billingError.value = '';
    try {
        await forceStoreResubscribe(currentShop.value, false);
        (window.top ?? window).location.href = billingSubscriptionUrl.value;
    } catch (error) {
        billingError.value = 'Something went wrong. Please try again or contact hello@customily.com.';
        window.captureException(error as Error);
        billingResolving.value = false;
    }
};

const openInstallLink = (_event: MouseEvent, region = '') => {
    if (isEtsyStore.value) setEtsyRequestToken(tokenSecret.value, tokenState.value, store.value);

    let newWindow = appInstallUrl.value;
    if (region) {
        newWindow += `&region=${region}`;
    }
    window.open(newWindow, '_blank');
};

const checkStoreAuthentication = async () => {
    if (!store.value || !user.value) {
        storeAuthenticated.value = true;
        return;
    }

    try {
        const shop = currentShop.value;
        const storeAuth = await isStoreAuthenticated(shop);

        if (!storeAuth.authenticated) {
            storeAuthenticated.value = false;
        } else {
            storeAuthenticated.value = true;
            if (window.Appcues) {
                const appCuesUsername = user.value?.customilyUser.Username + process.env.VUE_APP_APPCUES_SUFIX;
                commit('setShowShopifyFunction', storeAuth.showShopifyFunction);
                window.Appcues.identify(appCuesUsername, {
                    createdAt: new Date(user.value?.customilyUser?.CreatedDate as string).getTime(),
                    accountId: user.value?.customilyUser?.Id,
                    version: '2.0', // users on different versions may need to see different content
                    language: 'english', // for multi-language applications
                    stores: user.value?.customilyUser?.Stores.map((s) => `_${s.Type}_`).join(',')
                });
            }
        }

        isEtsyStore.value = checkIsEtsyStore(shop, user.value.customilyUser.Stores);
        if (isEtsyStore.value && !storeAuthenticated.value) {
            const etsyLogin = await getEtsyLogin();
            loginUrl.value = etsyLogin.url;
            tokenSecret.value = etsyLogin.codeVerification;
            tokenState.value = etsyLogin.state;
        }
        isAmazonStore.value = checkIsAmazonStore(shop, user.value.customilyUser.Stores);
    } catch (_error) {
        const error = _error as AxiosError;
        if (error.request.status !== 401) {
            window.captureException(error as Error);
        }
        storeAuthenticated.value = false;
    }
};

const checkStoreSelectorVisibility = (path: string) => {
    const allowAllStoresPaths = ['/sets'];
    const allowAllStoresSelector = allowAllStoresPaths.includes(path);

    /**
     * On routes that should not be able to select 'All Stores' option,
     * select the first available store if the current one is null
     */
    if (!allowAllStoresSelector && !store.value && user.value) {
        const url = user.value.customilyUser.Stores.find(
            (s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce || s.Type === StoreType.Shoplazza
        )?.Url;
        if (!url) {
            console.error('User does not have any Shopify stores!');
            return;
        }
        commit('setStore', url);
    }
};

const getUserData = async () => {
    if (isOpenRoute.value) return;

    try {
        await getUser();
        await checkStoreAuthentication();
    } catch {
        storeAuthenticated.value = false;
    }
};

watch(store, () => {
    checkStoreAuthentication();
});

const {
    route: {
        matched: [parent]
    }
} = router.resolve({ path: location.pathname });
isOpenRoute.value = route.meta?.isOpen || !parent?.meta?.requireStore;
omitClasses.value = route.meta?.omitClasses || parent?.meta?.omitClasses;
checkStoreSelectorVisibility(router.currentRoute.path);

document.addEventListener('auth-error', () => {
    storeAuthenticated.value = false;
});

getUserData();

onMounted(() => {
    router.beforeEach((to, _from, next) => {
        isOpenRoute.value = route.meta?.isOpen;
        checkStoreSelectorVisibility(to.path);
        next();
    });
});
</script>

<style lang="scss">
body {
    overflow: hidden;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}

.child-view {
    max-height: calc(100vh - 65px);
    max-height: calc(100dvh - 65px); /* For browsers supporting dynamic viewport units */
    padding-left: 15px;
    padding-right: 15px;
    padding-bottom: 3.85rem;
    overflow: auto;
}

#app {
    height: 100vh;
    font-family: Arial, sans-serif, 'Helvetica Neue', Helvetica;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
    display: flex;
    justify-content: center;

    .app-container {
        padding: 0 40px;
        width: 100%;

        &--wide {
            max-width: 1500px;
        }

        .auth-error-container {
            width: 100%;
            height: calc(100% - 65px);
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;

            .billing-wall {
                text-align: center;
            }
        }

        .store-selector {
            padding-left: 15px;
            top: 15px;

            &.auth-error {
                padding: 20px;
            }
        }

        .header {
            .title {
                margin-top: 30px;
                margin-bottom: 30px;
            }
        }
    }
}

#nav {
    padding: 20px;
    height: 65px;

    a {
        font-weight: bold;
        color: #2c3e50;

        &.router-link-exact-active {
            color: #42b983;
        }
    }
}

.woo-install-btn {
    background-color: #7157e0;
    border-bottom: 1px solid #7157e0;
    border-left: 1px solid #7157e0;
    border-radius: 4px;
    border-right: 1px solid #7157e0;
    border-top: 1px solid #7157e0;
    color: #ffffff;
    display: inline-block;
    font-family: 'Roboto', Tahoma, Verdana, Segoe, sans-serif;
    font-size: 16px;
    font-weight: 400;
    padding-bottom: 5px;
    padding-top: 5px;
    text-align: center;
    text-decoration: none;
    width: auto;
    word-break: keep-all;

    span {
        word-break: break-word;
        padding-left: 20px;
        padding-right: 20px;
        font-size: 16px;
        display: inline-block;
        letter-spacing: normal;

        span {
            word-break: break-word;
            line-height: 32px;
        }
    }
}

body {
    font-family: 'Roboto', Tahoma, Verdana, Segoe, sans-serif;
    color: #555555;
}

.content p {
    margin: 0;
    font-size: 17px;
}

.download-link {
    font-weight: bold;
}

.instructions {
    font-size: 10px;
    line-height: 1.2;
    text-align: left;
}

.instructions p {
    margin: 0;
}

.instructions .help {
    font-size: 14px;
    word-break: break-word;
}

.instructions a {
    text-decoration: underline;
    color: #7157e0;
}
</style>
